/**
=========================================================
* STAIL - Button Styles Utility (3 Categories Theme)
=========================================================
* Centralized button colors: RED (delete/cancel), GREEN (save/add), BLUE (print/preview/other)
* Updated: December 2, 2025 - Phase 4A Final
=========================================================
*/

import brandGradients from 'assets/theme/base/gradients';

/**
 * THREE BUTTON CATEGORIES - Covers all pages and popups:
 * 
 * 🔴 RED CATEGORY: Cancel, Delete, Remove, Close, Batal, Padam
 * 🟢 GREEN CATEGORY: Save, Add, Submit, Create, Simpan, Tambah
 * 🔵 BLUE CATEGORY: Print, Preview, View, Export, Edit, Other actions
 * 
 * Usage:
 * import { buttonStyles } from 'utils/buttonStyles';
 * <Button sx={buttonStyles.save}>Simpan</Button>
 */
export const buttonStyles = {
  // ========================================
  // 🟢 GREEN CATEGORY - Save/Add Functions
  // ========================================
  
  // Save/Simpan button
  save: {
    background: brandGradients.greenSuccess,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(0, 160, 32, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(0, 160, 32, 0.4)',
    },
  },

  // Add/Tambah button (same as save)
  add: {
    background: brandGradients.greenSuccess,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(0, 160, 32, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(0, 160, 32, 0.4)',
    },
  },

  // Submit/Create button (same as save)
  submit: {
    background: brandGradients.greenSuccess,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(0, 160, 32, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(0, 160, 32, 0.4)',
    },
  },

  // ========================================
  // 🔴 RED CATEGORY - Delete/Cancel Functions
  // ========================================
  
  // Delete/Padam button
  delete: {
    background: brandGradients.redError,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(160, 0, 0, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(160, 0, 0, 0.4)',
    },
  },

  // Cancel/Batal button (same as delete)
  cancel: {
    background: brandGradients.redError,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(160, 0, 0, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(160, 0, 0, 0.4)',
    },
  },

  // Remove/Close button (same as delete)
  remove: {
    background: brandGradients.redError,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(160, 0, 0, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(160, 0, 0, 0.4)',
    },
  },

  // ========================================
  // 🔵 BLUE CATEGORY - Print/Preview/Other Functions
  // ========================================
  
  // Print button
  print: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', // navy blue to bright blue
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // Preview button (same as print)
  preview: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // Primary/View/Other actions (same as print)
  primary: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // Edit/Update button (same as print - blue for other actions)
  edit: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // Export/View button (same as print)
  export: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // Special/Other actions (same as print)
  special: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
    },
  },

  // ========================================
  // OTHER STATES
  // ========================================
  
  // Secondary/Outline style (blue outline)
  secondary: {
    background: 'transparent',
    color: '#3b82f6',
    border: '2px solid #3b82f6',
    fontWeight: 'bold',
    '&:hover': {
      background: 'rgba(59, 130, 246, 0.1)',
      borderColor: '#1e3a8a',
    },
  },

  // Warning (kept for specific warning cases)
  warning: {
    background: brandGradients.orangeWarning,
    color: '#fff',
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(243, 156, 18, 0.3)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 6px 16px rgba(243, 156, 18, 0.4)',
    },
  },

  // Disabled state
  disabled: {
    background: '#e5e7eb',
    color: '#9ca3af',
    fontWeight: 'bold',
    cursor: 'not-allowed',
    '&:hover': {
      background: '#e5e7eb',
    },
  },
};

/**
 * Button size variants for consistent sizing.
 */
export const buttonSizes = {
  small: {
    padding: '6px 16px',
    fontSize: '0.875rem',
  },
  medium: {
    padding: '8px 20px',
    fontSize: '0.9375rem',
  },
  large: {
    padding: '10px 24px',
    fontSize: '1rem',
  },
};

/**
 * Combined button presets for common use cases.
 * These combine style + size for one-line usage.
 */
export const buttonPresets = {
  // Dialog action buttons
  dialogSave: {
    ...buttonStyles.save,
    ...buttonSizes.medium,
  },
  dialogCancel: {
    ...buttonStyles.cancel,
    ...buttonSizes.medium,
  },

  // Form action buttons
  formSubmit: {
    ...buttonStyles.save,
    ...buttonSizes.large,
  },
  formReset: {
    ...buttonStyles.cancel,
    ...buttonSizes.medium,
  },

  // Table action buttons
  tableAdd: {
    ...buttonStyles.save,
    ...buttonSizes.small,
  },
  tableEdit: {
    ...buttonStyles.edit,
    ...buttonSizes.small,
  },
  tableDelete: {
    ...buttonStyles.delete,
    ...buttonSizes.small,
  },
  tableView: {
    ...buttonStyles.primary,
    ...buttonSizes.small,
  },

  // Print/Export buttons
  printButton: {
    ...buttonStyles.print,
    ...buttonSizes.medium,
  },
  exportButton: {
    ...buttonStyles.primary,
    ...buttonSizes.medium,
  },
};

export default buttonStyles;
